import { Newspaper, Sparkles } from "lucide-react";
import { EmptyState } from "@/components/empty-state";
import { StockLogo } from "@/components/stock-logo";
import { SignatureBanner } from "@/components/signature-banner";
import { formatRelativeDate } from "@/components/news-list";
import { cn } from "@/lib/utils";
import type { DepotNewsItem } from "@/lib/portfolio/portfolio-news";
import type { PortfolioNewsInsight } from "@/lib/portfolio/portfolio-news-ai";

const RELEVANCE_DOT: Record<PortfolioNewsInsight["highlights"][number]["relevance"], string> = {
  positive: "bg-positive",
  negative: "bg-negative",
  neutral: "bg-muted-foreground",
};

/** Neuestes Element je Symbol - die verlinkte "Quelle" fuer eine KI-Highlight-Kachel, die
 * selbst kein eigenes Einzelartikel-Link fuehrt (die KI sieht nur Ueberschriften, siehe
 * portfolio-news-ai.ts-Docstring). `news` kommt bereits neueste-zuerst sortiert aus
 * loadPortfolioNews(). */
function latestItemBySymbol(news: DepotNewsItem[]): Map<string, DepotNewsItem> {
  const map = new Map<string, DepotNewsItem>();
  for (const item of news) if (!map.has(item.symbol)) map.set(item.symbol, item);
  return map;
}

/**
 * KI-Zusammenfassungs-Kachel fuer "Meine News" - das eigentliche Herzstueck des Tabs
 * (Erik: "eine grandiose KI Zusammenfassung geben"). [Punkt 21]: an das "Top 10 der
 * Woche"-Layout angelehnt (Dunkelblau/Gold-`SignatureBanner`, siehe [Punkt 8]) statt der
 * vorherigen, laut Erik "zu unauffälligen" hellen Gold-Card. Highlight-Kacheln sind jetzt
 * echte Links zur juengsten zugrunde liegenden Meldung je Position (`news`-Prop neu) statt
 * reiner Info-Divs.
 */
export function PortfolioNewsSummary({ insight, news }: { insight: PortfolioNewsInsight; news: DepotNewsItem[] }) {
  const latestBySymbol = latestItemBySymbol(news);

  return (
    <SignatureBanner variant="block" eyebrow="KI-Zusammenfassung für dein Depot">
      <div className="flex items-start gap-2">
        <Sparkles className="mt-1 h-5 w-5 shrink-0 text-gold" aria-hidden="true" />
        <p className="font-serif text-lg leading-relaxed">{insight.summary}</p>
      </div>
      {insight.highlights.length > 0 && (
        <div className="mt-4 grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
          {insight.highlights.map((h, i) => {
            const source = latestBySymbol.get(h.symbol);
            const Tile = source ? "a" : "div";
            return (
              <Tile
                key={`${h.symbol}-${i}`}
                {...(source ? { href: source.link, target: "_blank", rel: "noopener noreferrer" } : {})}
                className={cn(
                  "flex items-start gap-2.5 rounded-lg border border-gold/25 bg-primary-foreground/5 p-3",
                  source && "transition-colors hover:border-gold/60 hover:bg-primary-foreground/10",
                )}
              >
                <StockLogo symbol={h.symbol} logoUrl={h.logoUrl} size={20} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <p className="truncate text-xs font-medium text-primary-foreground">{h.companyName}</p>
                    <span
                      className={cn("h-1.5 w-1.5 shrink-0 rounded-full", RELEVANCE_DOT[h.relevance])}
                      aria-hidden="true"
                    />
                  </div>
                  <p className="mt-0.5 text-xs leading-snug text-primary-foreground/75">{h.theme}</p>
                </div>
              </Tile>
            );
          })}
        </div>
      )}
    </SignatureBanner>
  );
}

function NewsThumbnail({ item, className }: { item: DepotNewsItem; className: string }) {
  if (item.thumbnailUrl) {
    return (
      // eslint-disable-next-line @next/next/no-img-element -- externe, unbekannte Bildhosts, kein next/image-Whitelisting pro Publisher moeglich (gleiches Muster wie news-list.tsx).
      <img src={item.thumbnailUrl} alt="" className={cn(className, "object-cover")} />
    );
  }
  return (
    <div className={cn(className, "flex items-center justify-center bg-muted")}>
      <Newspaper className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
    </div>
  );
}

function NewsMeta({ item }: { item: DepotNewsItem }) {
  return (
    <p className="text-xs text-muted-foreground">
      {item.publisher}
      {item.publisher && item.publishedAt && " · "}
      {formatRelativeDate(item.publishedAt)}
    </p>
  );
}

/** Gruppiert den chronologischen Feed nach Aktie (backlog.md Punkt 7, "klar nach
 * Unternehmen gruppiert/erkennbar sein") - Map statt Sortierung, damit die Reihenfolge
 * innerhalb jeder Gruppe chronologisch (neueste zuerst, kommt bereits so aus
 * loadPortfolioNews()) bleibt UND die Gruppen-Reihenfolge selbst am Erscheinen der
 * jeweils NEUESTEN Meldung dieser Aktie haengt (die Aktie mit der aktuellsten Meldung
 * steht oben) statt z.B. alphabetisch. */
function groupBySymbol(news: DepotNewsItem[]): { symbol: string; companyName: string; logoUrl?: string; items: DepotNewsItem[] }[] {
  const order: string[] = [];
  const groups = new Map<string, { symbol: string; companyName: string; logoUrl?: string; items: DepotNewsItem[] }>();
  for (const item of news) {
    let group = groups.get(item.symbol);
    if (!group) {
      group = { symbol: item.symbol, companyName: item.companyName, logoUrl: item.logoUrl, items: [] };
      groups.set(item.symbol, group);
      order.push(item.symbol);
    }
    group.items.push(item);
  }
  return order.map((symbol) => groups.get(symbol)!);
}

/**
 * Der eigentliche Feed: nach Aktie gruppiert statt eines einzigen durchgemischten
 * chronologischen Stroms (backlog.md Punkt 7, 2026-07-30 - "auf einen Blick erkennbar,
 * welche die Microsoft-News und welche die PepsiCo-News sind"), aber bewusst weiterhin
 * EINE Seite/EIN Feed ohne Tabs oder eigene Nav-Abschnitte (Eriks frühere Ablehnung der
 * 3-Reiter-Idee bleibt gültig, siehe Git-Historie dieser Datei). [Punkt 22]: Kacheln
 * marginal vergrößert (Thumbnail/Schrift/Abstand) und Unternehmen zusätzlich direkt auf
 * JEDER Karte markiert (nicht nur in der Gruppen-Kopfzeile) - beim Scrollen mitten in einer
 * langen Gruppe war vorher nicht mehr auf den ersten Blick erkennbar, zu welcher Aktie eine
 * Karte gehört, ohne zurück zum Gruppenkopf zu schauen.
 */
export function PortfolioNewsFeed({ news }: { news: DepotNewsItem[] }) {
  if (news.length === 0) {
    return <EmptyState message="Aktuell keine News zu deinen Depot-Positionen gefunden." />;
  }

  const groups = groupBySymbol(news);

  return (
    <div className="flex flex-col gap-6">
      {groups.map((group) => (
        <div key={group.symbol} className="flex flex-col gap-2.5">
          <div className="flex items-center gap-2">
            <StockLogo symbol={group.symbol} logoUrl={group.logoUrl} size={24} />
            <span className="text-sm font-semibold text-ink">{group.companyName}</span>
          </div>
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
            {group.items.map((item, i) => (
              <a
                key={`${item.link}-${i}`}
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className="reveal group flex items-center gap-3 rounded-lg border border-hairline p-2.5 transition-colors hover:border-secondary/40"
              >
                <NewsThumbnail item={item} className="h-16 w-16 shrink-0 rounded-md" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[0.7rem] font-medium uppercase tracking-wide text-secondary">
                    {group.companyName}
                  </p>
                  <h4 className="mt-0.5 line-clamp-2 text-sm font-medium leading-snug text-ink group-hover:text-secondary">
                    {item.title}
                  </h4>
                  <NewsMeta item={item} />
                </div>
              </a>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
